import { BOIType } from "../../../src/app/core/interfaces";
import { Timestamp } from "firebase/firestore";

/**
 * Ensures all cases in a switch statement or similar control structure are handled.
 * If an unhandled case is encountered, this function throws an error with the provided message.
 *
 * @param {never} _param - The parameter that should never be passed to this function. Used to enforce exhaustiveness.
 * @param {string} errorMessage - Optional custom error message to throw. Defaults to
 *                                "Unhandled case in exhaustive check".
 *
 * @throws {Error} Throws an error if an unhandled case is encountered.
 */
export const exhaustiveCheck = (
  _param: never,
  errorMessage = "Unhandled case in exhaustive check"
): never => {
  throw new Error(errorMessage);
};

export const passBoiType = (boiType: BOIType, pass: BOIType | "all"): boolean => {
  if (pass === "all") return true;

  return boiType === pass;
};

export const convertTimestampsToDates = (obj: any): any => {
  if (obj && typeof obj === "object") {
    if (obj instanceof Timestamp) {
      return obj.toDate();
    }
    if (
      Object.prototype.hasOwnProperty.call(obj, "seconds") &&
      Object.prototype.hasOwnProperty.call(obj, "nanoseconds")
    ) {
      return new Timestamp(obj?.seconds, obj?.nanoseconds).toDate();
    }
    if (
      Object.prototype.hasOwnProperty.call(obj, "_seconds") &&
      Object.prototype.hasOwnProperty.call(obj, "_nanoseconds")
    ) {
      return new Timestamp(obj?._seconds, obj?._nanoseconds).toDate();
    }
    Object.keys(obj).forEach((key) => {
      obj[key] = convertTimestampsToDates(obj[key]);
    });
  }
  return obj;
};

export const convertDatesToTimestamps = <T>(obj: any): T => {
  if (obj instanceof Date) {
    return Timestamp.fromDate(obj) as any;
  }
  if (Array.isArray(obj)) {
    return obj.map(convertDatesToTimestamps) as any;
  }
  if (obj && typeof obj === "object") {
    const newObject = {} as any;
    Object.keys(obj).forEach((key) => {
      newObject[key] = convertDatesToTimestamps(obj[key]);
    });
    return newObject as T;
  }
  return obj;
};
